import React, { useEffect, useMemo, useState } from 'react';
import { apiClient } from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import {
    Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from '@/components/ui/select';
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import { AlertTriangle, Clock, User, Phone, ArrowRight, Loader2, RefreshCcw, X, RotateCcw } from 'lucide-react';

// Kanban columns (mirrors the main pipeline minus terminal stages).
const STAGES = [
    { id: 'new_lead',    label: 'New Leads',    accent: 'border-sky-500/40 bg-sky-500/5' },
    { id: 'no_answer',   label: 'No Answer',    accent: 'border-orange-500/40 bg-orange-500/5' },
    { id: 'call_back',   label: 'Call Back',    accent: 'border-violet-500/40 bg-violet-500/5' },
    { id: 'warm_lead',   label: 'Warm Leads',   accent: 'border-amber-500/40 bg-amber-500/5' },
    { id: 'hot_lead',    label: 'Hot Leads',    accent: 'border-red-500/40 bg-red-500/5' },
    { id: 'in_progress', label: 'In Progress',  accent: 'border-emerald-500/40 bg-emerald-500/5' },
];

const dayClass = (d) =>
    d >= 30 ? 'text-red-600 bg-red-500/10 border-red-500/30'
    : d >= 14 ? 'text-orange-500 bg-orange-500/10 border-orange-500/30'
    : 'text-amber-600 bg-amber-500/10 border-amber-500/30';

export default function StaleLeadsTab({ teams = [], agents = [] }) {
    // Threshold is fixed at 5 days per product spec (≥5 days no activity).
    const THRESHOLD = 5;
    const [filterTeam, setFilterTeam] = useState('all');
    const [filterAgent, setFilterAgent] = useState('all');
    const [filterStage, setFilterStage] = useState('all');
    const [searchTerm, setSearchTerm] = useState('');
    const [loading, setLoading] = useState(false);
    const [items, setItems] = useState([]);

    // Reassign dialog (per-card OR bulk)
    const [reassigning, setReassigning] = useState(false);
    const [activeLead, setActiveLead] = useState(null);
    const [reassignTarget, setReassignTarget] = useState('');
    const [reassignNote, setReassignNote] = useState('');

    // Bulk selection state
    const [selectedIds, setSelectedIds] = useState(() => new Set());
    const [showBulkDialog, setShowBulkDialog] = useState(false);

    const toggleSelect = (id) => {
        setSelectedIds(prev => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id); else next.add(id);
            return next;
        });
    };
    const clearSelection = () => setSelectedIds(new Set());

    const load = async () => {
        setLoading(true);
        try {
            const params = { threshold_days: THRESHOLD };
            if (filterTeam !== 'all')  params.team_id  = filterTeam;
            if (filterAgent !== 'all') params.agent_id = filterAgent;
            if (filterStage !== 'all') params.stage    = filterStage;
            const res = await apiClient.get('/sales/stale-leads', { params });
            setItems(res.data?.items || []);
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Failed to load dormant leads');
        } finally {
            setLoading(false);
        }
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => { load(); }, [filterTeam, filterAgent, filterStage]);

    const filteredItems = useMemo(() => {
        const term = searchTerm.trim().toLowerCase();
        if (!term) return items;
        return items.filter(r =>
            (r.full_name || '').toLowerCase().includes(term) ||
            (r.phone || '').toLowerCase().includes(term) ||
            (r.assigned_to_name || '').toLowerCase().includes(term)
        );
    }, [items, searchTerm]);

    const byStage = useMemo(() => {
        const map = Object.fromEntries(STAGES.map(s => [s.id, []]));
        filteredItems.forEach(r => {
            if (map[r.stage]) map[r.stage].push(r);
        });
        return map;
    }, [filteredItems]);

    const submitReassign = async () => {
        if (!activeLead || !reassignTarget) { toast.error('Pick a target agent'); return; }
        setReassigning(true);
        try {
            const res = await apiClient.post('/sales/stale-leads/reassign', {
                lead_ids: [activeLead.id],
                new_agent_id: reassignTarget,
                note: reassignNote,
            });
            toast.success(`Moved to ${res.data.new_agent_name} as a reassigned stale lead`);
            setActiveLead(null);
            setReassignTarget('');
            setReassignNote('');
            await load();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Reassignment failed');
        } finally {
            setReassigning(false);
        }
    };

    const submitBulkReassign = async () => {
        if (!reassignTarget) { toast.error('Pick a target agent'); return; }
        const ids = Array.from(selectedIds);
        if (!ids.length) { toast.error('No leads selected'); return; }
        setReassigning(true);
        try {
            const res = await apiClient.post('/sales/stale-leads/reassign', {
                lead_ids: ids,
                new_agent_id: reassignTarget,
                note: reassignNote,
            });
            const moved = (res.data?.reassigned || []).length;
            const skipped = res.data?.skipped || [];
            const skipReasons = skipped.reduce((acc, s) => {
                const reason = (s.reason || 'unknown').split(':')[0];
                acc[reason] = (acc[reason] || 0) + 1;
                return acc;
            }, {});
            const reasonLabel = {
                already_assigned: 'already on this agent',
                terminal_stage: 'already enrolled/rejected',
                not_found: 'not found',
            };
            const skipBreakdown = Object.entries(skipReasons)
                .map(([r, n]) => `${n} ${reasonLabel[r] || r}`)
                .join(', ');

            if (moved > 0) {
                toast.success(
                    `Reassigned ${moved} lead${moved === 1 ? '' : 's'} to ${res.data.new_agent_name} as New Lead`
                    + (skipped.length ? ` · ${skipped.length} skipped (${skipBreakdown})` : '')
                );
            } else if (skipped.length) {
                // Nothing moved — show a clear warning explaining why
                toast.warning(
                    `0 leads reassigned. All ${skipped.length} skipped: ${skipBreakdown}.`
                    + (skipReasons.already_assigned
                        ? ` These leads are already owned by ${res.data.new_agent_name} — pick a different target agent.`
                        : ''),
                    { duration: 7000 }
                );
            }
            setShowBulkDialog(false);
            setReassignTarget('');
            setReassignNote('');
            clearSelection();
            await load();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Bulk reassignment failed');
        } finally {
            setReassigning(false);
        }
    };

    const selectAllInColumn = (stageId, currentlyAllSelected) => {
        const cardIds = (byStage[stageId] || []).map(c => c.id);
        setSelectedIds(prev => {
            const next = new Set(prev);
            if (currentlyAllSelected) {
                cardIds.forEach(id => next.delete(id));
            } else {
                cardIds.forEach(id => next.add(id));
            }
            return next;
        });
    };

    const submitReactivate = async () => {
        const ids = Array.from(selectedIds);
        if (!ids.length) { toast.error('No leads selected'); return; }
        setReassigning(true);
        try {
            const res = await apiClient.post('/sales/stale-leads/reactivate', {
                lead_ids: ids,
                note: 'Re-activated from dormant pool — bulk action',
            });
            const moved = (res.data?.reactivated || []).length;
            const skipped = res.data?.skipped || [];
            if (moved > 0) {
                toast.success(
                    `Re-activated ${moved} lead${moved === 1 ? '' : 's'} as New Lead under their current owner`
                    + (skipped.length ? ` · ${skipped.length} skipped` : '')
                );
            } else {
                toast.warning(`0 leads re-activated. ${skipped.length} skipped.`);
            }
            clearSelection();
            await load();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Re-activate failed');
        } finally {
            setReassigning(false);
        }
    };

    const eligibleAgents = filterTeam === 'all'
        ? agents
        : agents.filter(a => a.team_id === filterTeam);

    return (
        <div className="space-y-4" data-testid="dormant-leads-tab">
            {/* Filters bar */}
            <div className="flex flex-wrap items-center gap-3">
                <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-amber-500/10 border border-amber-500/30 text-xs">
                    <Clock className="h-3.5 w-3.5 text-amber-600" />
                    <span className="text-amber-700 dark:text-amber-400 font-medium">No activity for ≥ {THRESHOLD} days · pre May 1, 2026 only</span>
                </div>

                {teams.length > 0 && (
                    <Select value={filterTeam} onValueChange={v => { setFilterTeam(v); setFilterAgent('all'); }}>
                        <SelectTrigger className="w-40 h-9 text-xs" data-testid="dormant-filter-team">
                            <SelectValue placeholder="All teams" />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">All teams</SelectItem>
                            {teams.map(t => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
                        </SelectContent>
                    </Select>
                )}

                <Select value={filterAgent} onValueChange={setFilterAgent}>
                    <SelectTrigger className="w-44 h-9 text-xs" data-testid="dormant-filter-agent">
                        <SelectValue placeholder="All agents" />
                    </SelectTrigger>
                    <SelectContent className="max-h-80">
                        <SelectItem value="all">All agents</SelectItem>
                        {eligibleAgents.map(a => (
                            <SelectItem key={a.id} value={a.id}>{a.full_name}</SelectItem>
                        ))}
                    </SelectContent>
                </Select>

                <Input
                    placeholder="Search name, phone, agent…"
                    value={searchTerm}
                    onChange={e => setSearchTerm(e.target.value)}
                    className="w-56 h-9 text-xs"
                    data-testid="dormant-search"
                />

                <Button variant="outline" size="sm" onClick={load} disabled={loading} data-testid="dormant-refresh">
                    {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCcw className="h-3.5 w-3.5" />}
                </Button>

                <div className="ml-auto flex items-center gap-2 px-3 py-1.5 rounded-lg bg-muted/40 border text-xs" data-testid="dormant-total">
                    <AlertTriangle className="h-3.5 w-3.5 text-amber-500" />
                    <span className="font-semibold">{filteredItems.length}</span>
                    <span className="text-muted-foreground">dormant lead{filteredItems.length === 1 ? '' : 's'}</span>
                </div>
            </div>

            {/* Kanban board */}
            <div className="flex gap-3 overflow-x-auto pb-3" data-testid="dormant-kanban">
                {STAGES.map(stage => {
                    const cards = byStage[stage.id] || [];
                    const selectedInCol = cards.filter(c => selectedIds.has(c.id)).length;
                    const allSelectedInCol = cards.length > 0 && selectedInCol === cards.length;
                    return (
                        <div
                            key={stage.id}
                            className={`flex-shrink-0 w-72 rounded-lg border ${stage.accent}`}
                            data-testid={`dormant-col-${stage.id}`}
                        >
                            <div className="flex items-center justify-between px-3 py-2 border-b border-current/10">
                                <div className="flex items-center gap-2">
                                    {cards.length > 0 && (
                                        <Checkbox
                                            checked={allSelectedInCol}
                                            onCheckedChange={() => selectAllInColumn(stage.id, allSelectedInCol)}
                                            data-testid={`dormant-col-select-all-${stage.id}`}
                                            aria-label={`Select all leads in ${stage.label}`}
                                        />
                                    )}
                                    <h3 className="text-sm font-semibold">{stage.label}</h3>
                                </div>
                                <Badge variant="outline" className="text-[10px] font-mono">{cards.length}</Badge>
                            </div>
                            <div className="p-2 space-y-2 max-h-[calc(100vh-280px)] overflow-y-auto">
                                {loading ? (
                                    <div className="text-center py-8 text-xs text-muted-foreground"><Loader2 className="h-4 w-4 inline animate-spin mr-1.5" />Loading…</div>
                                ) : cards.length === 0 ? (
                                    <div className="text-center py-8 text-xs text-muted-foreground italic">No dormant leads</div>
                                ) : cards.map(r => {
                                    const isSelected = selectedIds.has(r.id);
                                    return (
                                    <Card
                                        key={r.id}
                                        className={`bg-card hover:shadow-md transition-shadow ${isSelected ? 'ring-2 ring-amber-500' : ''}`}
                                        data-testid={`dormant-card-${r.id}`}
                                    >
                                        <CardContent className="p-3 space-y-2">
                                            <div className="flex items-start justify-between gap-2">
                                                <div className="flex items-start gap-2 min-w-0 flex-1">
                                                    <Checkbox
                                                        checked={isSelected}
                                                        onCheckedChange={() => toggleSelect(r.id)}
                                                        className="mt-0.5"
                                                        data-testid={`dormant-checkbox-${r.id}`}
                                                        aria-label={`Select ${r.full_name}`}
                                                    />
                                                    <div className="min-w-0 flex-1">
                                                        <p className="font-medium text-sm truncate" title={r.full_name}>{r.full_name}</p>
                                                        {r.phone && (
                                                            <p className="font-mono text-[11px] text-muted-foreground inline-flex items-center gap-1 mt-0.5">
                                                                <Phone className="h-3 w-3" />{r.phone}
                                                            </p>
                                                        )}
                                                    </div>
                                                </div>
                                                <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded border ${dayClass(r.days_stale)}`}>
                                                    {r.days_stale}d
                                                </span>
                                            </div>
                                            <div className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
                                                <User className="h-3 w-3" />
                                                <span className="truncate" title={r.assigned_to_name}>{r.assigned_to_name}</span>
                                                {r.team_name && <span className="ml-auto text-[10px]">{r.team_name}</span>}
                                            </div>
                                            {r.is_reassigned_stale_lead && (
                                                <Badge variant="outline" className="text-[9px] bg-amber-500/10 text-amber-600 border-amber-500/30">
                                                    reassigned stale lead
                                                </Badge>
                                            )}
                                            <Button
                                                size="sm" variant="outline"
                                                className="w-full h-7 text-[11px] border-amber-500/40 text-amber-700 hover:bg-amber-500/10"
                                                onClick={() => { setActiveLead(r); setReassignTarget(''); setReassignNote(''); }}
                                                data-testid={`dormant-reassign-${r.id}`}
                                            >
                                                <ArrowRight className="h-3 w-3 mr-1" />
                                                Reassign
                                            </Button>
                                        </CardContent>
                                    </Card>
                                    );
                                })}
                            </div>
                        </div>
                    );
                })}
            </div>

            {/* Sticky bulk-action bar — appears whenever 1+ leads selected */}
            {selectedIds.size > 0 && (
                <div
                    className="fixed bottom-6 left-1/2 -translate-x-1/2 z-40 flex items-center gap-3 px-4 py-3 rounded-full bg-amber-600 text-white shadow-2xl border border-amber-500"
                    data-testid="dormant-bulk-bar"
                >
                    <span className="text-sm font-semibold" data-testid="dormant-bulk-count">
                        {selectedIds.size} selected
                    </span>
                    <Button
                        size="sm"
                        className="bg-emerald-500 text-white hover:bg-emerald-600 h-8 border border-emerald-400"
                        onClick={submitReactivate}
                        disabled={reassigning}
                        data-testid="dormant-bulk-reactivate-btn"
                        title="Move selected dormant leads back to New Leads under their current owner"
                    >
                        {reassigning
                            ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                            : <RotateCcw className="h-3.5 w-3.5 mr-1.5" />}
                        Re-activate
                    </Button>
                    <Button
                        size="sm"
                        className="bg-white text-amber-700 hover:bg-amber-50 h-8"
                        onClick={() => { setReassignTarget(''); setReassignNote(''); setShowBulkDialog(true); }}
                        data-testid="dormant-bulk-reassign-btn"
                    >
                        <ArrowRight className="h-3.5 w-3.5 mr-1.5" />
                        Bulk Reassign
                    </Button>
                    <Button
                        size="sm"
                        variant="ghost"
                        className="h-8 text-white hover:bg-amber-700 hover:text-white"
                        onClick={clearSelection}
                        data-testid="dormant-bulk-clear"
                    >
                        <X className="h-3.5 w-3.5 mr-1" />
                        Clear
                    </Button>
                </div>
            )}

            {/* Reassign dialog */}
            <Dialog open={!!activeLead} onOpenChange={o => { if (!o) setActiveLead(null); }}>
                <DialogContent data-testid="dormant-reassign-dialog">
                    <DialogHeader>
                        <DialogTitle>Reassign dormant lead</DialogTitle>
                        <DialogDescription>
                            {activeLead && (
                                <>
                                    <span className="font-medium text-foreground">{activeLead.full_name}</span>
                                    <span className="text-muted-foreground"> · {activeLead.phone || 'no phone'} · </span>
                                    <span className="text-muted-foreground">{activeLead.days_stale}d dormant · currently with {activeLead.assigned_to_name}</span>
                                </>
                            )}
                            <div className="mt-2 text-xs">
                                It will be tagged
                                <span className="px-1.5 mx-1 rounded text-[11px] bg-amber-500/15 text-amber-600 border border-amber-500/30">reassigned stale lead</span>
                                and reset to <strong>New Lead</strong> stage so the new owner starts fresh.
                            </div>
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-3">
                        <div>
                            <label className="text-xs font-medium text-muted-foreground">Reassign to</label>
                            <Select value={reassignTarget} onValueChange={setReassignTarget}>
                                <SelectTrigger className="mt-1" data-testid="dormant-reassign-target">
                                    <SelectValue placeholder="Pick an agent" />
                                </SelectTrigger>
                                <SelectContent className="max-h-80">
                                    {agents.map(a => (
                                        <SelectItem key={a.id} value={a.id}>
                                            {a.full_name}
                                            {a.team_name ? <span className="text-muted-foreground"> · {a.team_name}</span> : null}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div>
                            <label className="text-xs font-medium text-muted-foreground">Note (optional)</label>
                            <Input
                                placeholder="Why are you reassigning this lead?"
                                value={reassignNote}
                                onChange={e => setReassignNote(e.target.value)}
                                className="mt-1"
                                data-testid="dormant-reassign-note"
                            />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setActiveLead(null)} disabled={reassigning}>Cancel</Button>
                        <Button
                            onClick={submitReassign}
                            disabled={reassigning || !reassignTarget}
                            className="bg-amber-600 hover:bg-amber-700 text-white"
                            data-testid="dormant-reassign-submit"
                        >
                            {reassigning ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <ArrowRight className="h-4 w-4 mr-1.5" />}
                            Confirm reassign
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Bulk reassign dialog */}
            <Dialog open={showBulkDialog} onOpenChange={o => { if (!o) setShowBulkDialog(false); }}>
                <DialogContent data-testid="dormant-bulk-reassign-dialog">
                    <DialogHeader>
                        <DialogTitle>Bulk reassign {selectedIds.size} dormant lead{selectedIds.size === 1 ? '' : 's'}</DialogTitle>
                        <DialogDescription>
                            All selected leads will be tagged
                            <span className="px-1.5 mx-1 rounded text-[11px] bg-amber-500/15 text-amber-600 border border-amber-500/30">reassigned stale lead</span>
                            and reset to <strong>New Lead</strong> stage on the target agent's pipeline, with a fresh activity timestamp so they show up as New Leads under that agent.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-3">
                        <div>
                            <label className="text-xs font-medium text-muted-foreground">Reassign to</label>
                            <Select value={reassignTarget} onValueChange={setReassignTarget}>
                                <SelectTrigger className="mt-1" data-testid="dormant-bulk-reassign-target">
                                    <SelectValue placeholder="Pick an agent" />
                                </SelectTrigger>
                                <SelectContent className="max-h-80">
                                    {agents.map(a => (
                                        <SelectItem key={a.id} value={a.id}>
                                            {a.full_name}
                                            {a.team_name ? <span className="text-muted-foreground"> · {a.team_name}</span> : null}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div>
                            <label className="text-xs font-medium text-muted-foreground">Note (optional)</label>
                            <Input
                                placeholder="Why are you reassigning these leads?"
                                value={reassignNote}
                                onChange={e => setReassignNote(e.target.value)}
                                className="mt-1"
                                data-testid="dormant-bulk-reassign-note"
                            />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowBulkDialog(false)} disabled={reassigning}>Cancel</Button>
                        <Button
                            onClick={submitBulkReassign}
                            disabled={reassigning || !reassignTarget}
                            className="bg-amber-600 hover:bg-amber-700 text-white"
                            data-testid="dormant-bulk-reassign-submit"
                        >
                            {reassigning ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <ArrowRight className="h-4 w-4 mr-1.5" />}
                            Reassign {selectedIds.size} lead{selectedIds.size === 1 ? '' : 's'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
